import { DEFAULT_MESSAGE_TTL, SHORT_TIME_INTERVAL, TIMER_INTERVAL } from "./constants";
import Log from "./models/Log";
import { LogLevel } from "./models/LogLevel";
import { LogMetadata } from "./models/LogMetadata";
import TelemetryClientData from "./models/TelemetryClientData";
import TelemetryClient from "./TelemetryClient";
import TelemetryProcessor from "./TelemetryProcessor";

export default class Logger {
  private static telemetryProcessor: TelemetryProcessor<Log>;
  private static fastTelemetryProcessor: TelemetryProcessor<Log>;
  private static readonly APIEndpoint = "/v1/tracking-events";

  private constructor(private origin: string) {}

  /**
   * Function to get an instance of TelemetryProcessor
   */
  private static processor(): TelemetryProcessor<Log> {
    if (!Logger.telemetryProcessor) {
      if (!TelemetryClient.isInitialized) {
        throw new Error("Initialize TelemetryClient before using the logger");
      }
      Logger.telemetryProcessor = new TelemetryProcessor<Log>(
        "logs",
        TIMER_INTERVAL,
        this.addTenantInfoOnceAuthenticated.bind(this),
        this.APIEndpoint,
      );
    }
    return Logger.telemetryProcessor;
  }

  /**
   * Function to get an instance of fasterTelemetryProcessor
   */
  private static fastProcessor(): TelemetryProcessor<Log> {
    if (!Logger.fastTelemetryProcessor) {
      if (!TelemetryClient.isInitialized) {
        throw new Error("Initialize TelemetryClient before using the logger");
      }
      Logger.fastTelemetryProcessor = new TelemetryProcessor<Log>(
        "logs",
        SHORT_TIME_INTERVAL,
        this.addTenantInfoOnceAuthenticated.bind(this),
        this.APIEndpoint,
      );
    }
    return Logger.fastTelemetryProcessor;
  }

  /**
   * Function to get an instance of Logger
   * @param origin: where is the log request coming from. Eg. "Compute Plugin"
   */
  public static getLogger(origin: string) {
    return new Logger(origin);
  }

  private logToBrowser(logLevel: LogLevel, message: string) {
    switch (logLevel) {
      case LogLevel.Debug:
        console.debug(message);
        break;
      case LogLevel.Error:
        console.error(message);
        break;
      case LogLevel.Info:
        console.info(message);
        break;
      case LogLevel.Warning:
        console.warn(message);
        break;
      default:
        console.log(logLevel, message);
    }
  }

  /**
   * Function used by the user to send a log
   * @param logLevel info, warn, error or debug
   * @param message
   * @param logMetadata any metadata such as region, environment or any other data
   */
  public log(logLevel: LogLevel, message: string, logMetadata?: LogMetadata) {
    if (!TelemetryClient.config) {
      return;
    }
    if (TelemetryClient.config.logToBrowsersConsole) {
      this.logToBrowser(logLevel, message);
    }
    if (!TelemetryClient.config.postToTelemetryService) {
      return;
    }
    const log: Log = {
      logLevel: logLevel,
      msg: message,
      origin: this.origin,
      namespace: TelemetryClient.config.namespace,
      ...logMetadata,
    };
    const data: TelemetryClientData<Log> = {
      TTL: new Date().getTime() + DEFAULT_MESSAGE_TTL,
      payload: log,
    };
    if (logLevel === LogLevel.Error) {
      Logger.fastProcessor().add(data);
    } else {
      Logger.processor().add(data);
    }
  }

  public info(message: string) {
    this.log(LogLevel.Info, message);
  }

  public error(message: string) {
    this.log(LogLevel.Error, message);
  }

  public debug(message: string) {
    this.log(LogLevel.Debug, message);
  }

  public warn(message: string) {
    this.log(LogLevel.Warning, message);
  }

  /**
   * Replaces urls such as
   * "https://domain.com/#id_token..., https://domain.com/?id_token..., https://domain.com/id_token...
   * with "https://domain.com/"
   * @param input: url or message
   * @returns string: sanitized url or message
   */
  private static removeAuthInfo(input: string): string {
    if (!input.includes("id_token")) {
      return input;
    }
    return input.replace(/\/(#|\?|i)(.*)/, "/");
  }

  /**
   * Information to add to the log once the user authenticates
   * @param payload: log
   */
  private static async addTenantInfoOnceAuthenticated(payload: Log): Promise<Log> {
    payload = {
      currentPage: Logger.removeAuthInfo(window.location.href),
      event: "LogEvent",
      sessionId: TelemetryClient.config.authInstance.SessionId,
      ts: new Date().getTime(),
      ...payload,
    };
    return payload;
  }
}
